const namespaced = true;

const state = {
    substance: null,
    product: null,
    polygons: [],
    samples: [],
    grid: {
        type: "FeatureCollection",
        features: [],
    },
    bounds: null,
    loading: false,
    working: false,
    amounts: [],
    clayAmounts: [],
    humusAmounts: [],
    heightAmounts: [],
    topologyAmounts: [],
};

const initialState = JSON.parse(JSON.stringify(state));

const getters = {
    exportState: (state) => getExportState(state),
    substance: (state) => state.substance,
    samples: (state) => state.samples,
    polygons: (state) => state.polygons,
    product: (state) => state.product,
    grid: (state) => state.grid,
    loading: (state) => state.loading,
    working: (state) => state.working,
    topologyAmounts: (state) => state.topologyAmounts,
    clayAmounts: (state) => state.clayAmounts,
    humusAmounts: (state) => state.humusAmounts,
    heightAmounts: (state) => state.heightAmounts,
    bounds: (state) => state.bounds,
    type: (state) => (state.substance === "chalk" ? "ph" : state.substance),
    gridLoaded: (state) => state.grid.features.length,
    typeText: (state) => getTypeText(state.substance),
    totalArea: (state) => getTotalArea(state.grid.features),
    givaBounds: (state) => getGivaBounds(state.grid.features),
    valueBounds: (state) => getValueBounds(state.grid.features),
    totalGiva: (state) => getTotalGiva(state.grid.features),
    areaError: (state) => getAreaError(state.grid.features, state.substance),
    areas: (state) => getAreas(state.amounts, state.grid.features),
    avgGiva: (state) => getAvgGiva(state.grid.features),

    amountsWithArea: (state, getters) =>
        getAmountsAndArea(state.amounts, getters.areas),
    colorGrading: (state, getters, rootState, rootGetters) =>
        getColorGrading(getters.givaBounds, getters.totalArea, rootGetters),
    colors: (state, getters, rootState, rootGetters) =>
        getColors(getters.colorGrading, rootGetters),
    info: (state, getters, rootState, rootGetters) =>
        getInfo(state, getters, rootGetters),
};

const actions = {
    INITALIZE({ commit, dispatch }, { product, substance, features }) {
        return new Promise((resolve, reject) => {
            dispatch("SET_PRODUCT", product);
            dispatch("SET_SUBSTANCE", substance);
            dispatch("SET_FEATURES", features);
            resolve();
        });
    },

    SET_SUBSTANCE({ commit, state, dispatch }, substance) {
        if (state.grid.features.length) {
            dispatch("CLEAR_GRID_AND_SETTINGS");
        }
        commit("setSubstance", substance);
    },

    SET_PRODUCT({ commit }, product) {
        commit("setProduct", product);
    },

    SET_SAMPLES({ commit }, samples) {
        commit("setSamples", samples);
    },

    SET_POLYGONS({ commit, dispatch }, polygons) {
        if (polygons.length) {
            dispatch("CLEAR_POLYGONS");
            dispatch("CLEAR_GRID_AND_SETTINGS");
        }
        commit("setBounds", polygons);
        commit("setPolygons", polygons);
    },

    UPDATE_GRID({ commit }, gridLayer) {
        let features = Object.entries(gridLayer._layers).map(
            (layer) => layer[1].feature
        );
        commit("setGrid", JSON.parse(JSON.stringify(features)));
    },

    SET_FEATURES({ commit }, features) {
        commit("setGrid", features);
    },

    UPDATE_CLAY_AMOUNTS({ commit }, amounts) {
        commit("updateClayAmounts", amounts);
    },

    ADD_CLAY_AMOUNT({ commit }, amount) {
        commit("addClayAmount", amount);
    },

    UPDATE_HUMUS_AMOUNTS({ commit }, amounts) {
        commit("updateHumusAmounts", amounts);
    },

    ADD_HUMUS_AMOUNT({ commit }, amount) {
        commit("addHumusAmount", amount);
    },

    UPDATE_HEIGHT_AMOUNTS({ commit }, amounts) {
        commit("updateHeightAmounts", amounts);
    },

    ADD_HEIGHT_AMOUNT({ commit }, amounts) {
        commit("addHeightAmount", amounts);
    },

    UPDATE_TOPOLOGY_AMOUNTS({ commit }, amounts) {
        commit("updateTopologyAmounts", amounts);
    },

    ADD_TOPOLOGY_AMOUNT({ commit }, amounts) {
        commit("addTopologyAmounts", amounts);
    },

    ADJUST_GIVA({ commit, getters }) {
        const worker = new Worker(
            new URL("../../utils/giva-adjustment.js", import.meta.url),
            {
                type: "module",
                name: "giva-adjustment",
            }
        );
        worker.onmessage = (e) => {
            commit(e.data.type, e.data.payload);
        };
        if (getters.grid.features.length) {
            worker.postMessage({
                grid: getters.grid,
                clayAmounts: getters.clayAmounts,
                humusAmounts: getters.humusAmounts,
                heightAmounts: getters.heightAmounts,
                topologyAmounts: getters.topologyAmounts,
            });
        }
    },

    REMOVE_AMOUNTS_OUT_OF_BOUNDS({ state, commit, getters }) {
        commit(
            "setAmounts",
            state.amounts.filter((amount) => {
                return (
                    amount.value >= getters.valueBounds.min &&
                    amount.value <= getters.valueBounds.max
                );
            })
        );
    },

    CLEAR_STATE({ commit }) {
        commit("clearState");
    },

    CLEAR_POLYGONS({ commit }) {
        commit("setPolygons", []);
        commit("setBounds", []);
    },

    CLEAR_GRID_AND_SETTINGS({ commit, dispatch }) {
        commit("emtpyGrid");
        dispatch("manualGiva/CLEAR_STATE", {}, { root: true });
        dispatch("adjustments/CLEAR_STATE", {}, { root: true });
    },

    SET_STATE({ commit }, newState) {
        commit("setState", newState);
    },
};

const mutations = {
    setSamples(state, samples) {
        state.samples = samples;
    },

    setSubstance(state, substance) {
        state.substance = substance;
    },

    setProduct(state, product) {
        state.product = product;
    },

    setBounds(state, polygons) {
        let bounds = null;
        polygons.forEach((polygon) => {
            let newBounds = L.geoJson(polygon.geometry).getBounds();
            if (bounds) {
                bounds.extend(newBounds);
            } else {
                bounds = newBounds;
            }
        });
        state.bounds = bounds;
    },

    setPolygons(state, polygons) {
        state.polygons = polygons;
    },

    setLoading(state, status) {
        state.loading = status;
    },

    setWorking(state, status) {
        state.working = status;
    },

    addFeaturesToGrid(state, { features, polygon_id }) {
        features.forEach((cell, index) => {
            cell.properties.polygon_id = polygon_id;
            // cell.properties.id = index;
        });
        state.grid.features = state.grid.features.concat(features);
    },

    updateClayAmounts(state, amounts) {
        state.clayAmounts = JSON.parse(JSON.stringify(amounts));
    },

    addClayAmount(state, amount) {
        if (
            !state.clayAmounts.find(
                (clayAmount) => clayAmount.value == amount.value
            )
        ) {
            state.clayAmounts.push(amount);
        }
    },

    updateHumusAmounts(state, amounts) {
        state.humusAmounts = JSON.parse(JSON.stringify(amounts));
    },

    addHumusAmount(state, amount) {
        if (
            !state.heightAmounts.find(
                (humusAmount) => humusAmount.value == amount.value
            )
        ) {
            state.heightAmounts.push(amount);
        }
    },

    updateTopologyAmounts(state, amounts) {
        state.topologyAmounts = JSON.parse(JSON.stringify(amounts));
    },

    addTopologyAmount(state, amount) {
        if (
            !state.topologyAmounts.find(
                (topologyAmount) => topologyAmount.value == amount.value
            )
        ) {
            state.topologyAmounts.push(amount);
        }
    },

    updateHeightAmounts(state, amounts) {
        state.heightAmounts = JSON.parse(JSON.stringify(amounts));
    },

    addHeightAmount(state, amount) {
        if (
            !state.heightAmounts.find(
                (heightAmount) => heightAmount.value == amount.value
            )
        ) {
            state.heightAmounts.push(amount);
        }
    },

    setAmounts(state, amounts) {
        state.amounts = amounts;
    },

    emtpyGrid(state) {
        state.grid.features = [];
    },

    setGrid(state, features) {
        features.forEach((element) => {
            element.properties.orgGiva = element.properties.giva
                ? element.properties.giva
                : 0;
        });
        state.grid.features = features;
    },

    updateGrid(state, features) {
        state.grid.features = features;
    },

    clearState(state) {
        Object.keys(state).forEach((key) => {
            state[key] = initialState[key];
        });
    },

    setState(state, newState) {
        Object.keys(newState).forEach((key) => {
            state[key] = newState[key];
        });
    },
};

function getAreaError(features, substance) {
    return (
        features.reduce((area, cell) => {
            if (
                isCellValueZero(cell, substance) &&
                cell.properties.giva === 0 &&
                !cell.properties.givaManuallyAdded
            ) {
                return (area += cell.properties.area);
            }
            return area;
        }, 0) / 10000
    );
}

function isCellValueZero(cell, substance) {
    if (substance === "chalk") {
        return (
            cell.properties.ph === 0 ||
            cell.properties.mullhalt === 0 ||
            cell.properties.lerhalt === 0
        );
    } else {
        return cell.properties.value === 0;
    }
}

function getAvgGiva(features) {
    let totalGiva = 0;
    let nbrCellsWithValue = 0;

    features.forEach((cell) => {
        if (cell.properties.value != 0) {
            totalGiva += cell.properties.giva;
            nbrCellsWithValue += 1;
        }
    });

    return +(totalGiva / nbrCellsWithValue).toFixed(2);
}

function getAreas(amounts, features) {
    let areas = {};
    let closestValue;
    amounts.forEach((amount) => {
        areas[amount.value] = 0;
    });

    if (amounts.length) {
        features.forEach((feature) => {
            closestValue = amounts
                .map((amount) => amount.value)
                .reduce(function (prev, curr) {
                    return Math.abs(curr - feature.properties.value) <
                        Math.abs(prev - feature.properties.value)
                        ? curr
                        : prev;
                });

            areas[closestValue] += feature.properties.area;
        });
    }

    return areas;
}

function getAmountsAndArea(amounts, areas) {
    return amounts
        .map((amount) => {
            return {
                value: amount.value,
                amount: amount.amount,
                area: areas[amount.value] / 10000,
            };
        })
        .sort((a, b) => a.value - b.value);
}

function getTypeText(type) {
    switch (type) {
        case "ph":
            return "pH";
        case "p_al":
            return "Fosfor";
        case "k_al":
            return "Kalium";
        case "mg_al":
            return "Magnesium";
        case "magnesiumbehov":
            return "Magnesiumbehov";
        case "ca_al":
            return "Kalcium";
        case "chalk":
            return "Kalk (pH)";
    }
}

function getExportState(state) {
    return {
        substance: state.substance,
        product: state.product,
        polygons: state.polygons,
        bounds: state.bounds,
        loading: state.loading,
        amounts: state.amounts,
    };
}

function getValueBounds(features) {
    return features.reduce(
        (bounds, feature) => {
            if (feature.properties.value > bounds.max) {
                bounds.max = feature.properties.value;
            }
            if (
                feature.properties.value < bounds.min &&
                feature.properties.value > 0
            ) {
                bounds.min = feature.properties.value;
            }
            return bounds;
        },
        { min: 1000, max: 0 }
    );
}

function getTotalArea(features) {
    return features.reduce((sum, feature) => {
        return (sum += feature.properties.area / 10000);
    }, 0);
}

function getGivaBounds(features) {
    let min = 0;
    let max = 0;
    if (features.length) {
        min = Number.MAX_SAFE_INTEGER;
        features.forEach((feature) => {
            if (!feature.modified && feature.properties.giva < min) {
                min = feature.properties.giva;
            }
            if (!feature.modified && feature.properties.giva > max) {
                max = feature.properties.giva;
            }
        });
    }
    return {
        min: min,
        max: max,
    };
}

function getColors(colorGrading, rootgetters) {
    if (colorGrading) {
        if (colorGrading.length === 1 && colorGrading[0].value > 0) {
            return [
                {
                    from: colorGrading[0].value,
                    to: null,
                    color: rootgetters["colors/precisionColors"][
                        rootgetters["colors/precisionColors"].length - 1
                    ],
                },
            ];
        } else if (colorGrading.length === 1 && colorGrading[0].value == 0) {
            return [
                {
                    from: 0,
                    to: null,
                    color: rootgetters["colors/precisionColors"][0],
                },
            ];
        }
        return colorGrading.map((grade, i) => {
            return {
                from: grade ? grade.value : null,
                to: colorGrading[i + 1] ? colorGrading[i + 1].value : null,
                color: rootgetters["colors/precisionColors"][i],
            };
        });
    }
}

function getColorGrading(givaBounds, totalArea, rootgetters) {
    // If there are no min and max values return 0
    if (givaBounds.min == givaBounds.max) {
        return [0].map((grade) => {
            return { value: givaBounds.min, area: totalArea.toFixed(1) };
        });
    }

    let old = givaBounds.min;
    let length = 8;
    let colorGrading = [];

    colorGrading[0] = 0;
    colorGrading[1] = givaBounds.min;

    for (var i = 2; i < length + 1; i++) {
        colorGrading[i] = (givaBounds.max - givaBounds.min) / length + old;
        old = colorGrading[i];
    }
    let useDecimals = rootgetters["colors/precisionColors"];
    colorGrading = colorGrading.map((grade, i) => {
        return {
            value:
                useDecimals && grade !== null
                    ? +parseFloat(grade).toFixed(2)
                    : grade,
            area: getAreaForGrade(colorGrading, i, grade, state.grid),
        };
    });

    return colorGrading;
}

function getAreaForGrade(colorGrading, i, grade, grid) {
    let upperboundExist = colorGrading.length > i + 1;
    let areaSum = grid.features.reduce((sum, feature) => {
        // Is the giva within the grade-bounds?
        if (grade === null && feature.properties.giva === 0) {
            return sum + feature.properties.area;
        } else if (
            grade === 0
                ? feature.properties.giva > grade
                : feature.properties.giva >= grade
        ) {
            if (
                upperboundExist
                    ? feature.properties.giva < colorGrading[i + 1]
                    : true
            ) {
                return sum + feature.properties.area;
            }
        }
        return sum;
    }, 0);

    return (areaSum / 10000).toFixed(2);
}

function getTotalGiva(features) {
    return features.reduce((sum, feature) => {
        return (sum +=
            feature.properties.giva * (feature.properties.area / 10000));
    }, 0);
}

function getInfo(state, getters, rootGetters) {
    let zeroArea = state.grid.features.reduce((sum, feature) => {
        return (sum +=
            feature.properties.giva == 0 ? feature.properties.area / 10000 : 0);
    }, 0);

    return {
        totalArea: +getters.totalArea.toFixed(1) + " ha",
        upperBound:
            getters.givaBounds.max > 1000
                ? (getters.givaBounds.max / 1000).toFixed(1) + " ton/ha"
                : (getters.givaBounds.max
                      ? getters.givaBounds.max.toFixed(1)
                      : 0) + " kg/ha",
        lowerBound:
            getters.givaBounds.min > 1000
                ? (getters.givaBounds.min / 1000).toFixed(1) + " ton/ha"
                : (getters.givaBounds.min
                      ? getters.givaBounds.min.toFixed(1)
                      : 0) + " kg/ha",
        totalAmount: (getters.totalGiva / 1000).toFixed(1) + " ton",
        zeroArea:
            +zeroArea.toFixed(1) +
            " ha (" +
            +((zeroArea / getters.totalArea) * 100).toFixed(1) +
            "%)",
        method: "Anpassad styrfil",
        productTitle: getters.product ? getters.product.title : null,
        grades: getters.colors.map((grade, i) => {
            return {
                color: grade.color,
                area: getters.colorGrading[i].area,
                value:
                    grade.from.toFixed(1) +
                    " - " +
                    (grade.to == null
                        ? getters.givaBounds.max.toFixed(1)
                        : grade.to.toFixed(1)),
            };
        }),
        manualGrades: rootGetters["manualGiva/appliedGivorSorted"].map(
            (giva) => {
                let color;
                let index = rootGetters[
                    "manualGiva/appliedGivorSorted"
                ].indexOf(giva.toString());
                if (index !== -1) {
                    index =
                        rootGetters["manualGiva/colors"].length -
                        (rootGetters["manualGiva/appliedGivorSorted"].length -
                            index);
                    color = rootGetters["manualGiva/colors"][index];
                }
                return {
                    value: giva,
                    color: color,
                };
            }
        ),
    };
}

export default {
    namespaced,
    state,
    getters,
    actions,
    mutations,
};
